// Kimi AI Provider
// Handles Kimi K2 Turbo and other Moonshot AI models

// Global abort controller for canceling requests
let currentAbortController = null

export function stopKimi() {
  if (currentAbortController) {
    currentAbortController.abort()
    currentAbortController = null
  }
}

export async function callKimi(messages, userApiKey, model = 'kimi-k2-turbo-preview') {
  currentAbortController = new AbortController()
  
  console.log('🟡 Calling Kimi AI with model:', model)
  console.log('Context window: 256k tokens, Ultra-fast responses')
  
  // If the user provided an API key, call Kimi directly from the browser
  if (userApiKey) {
    try {
      const result = await callKimiDirect(messages, userApiKey, model, currentAbortController.signal)
      console.log('✅ Received response from Kimi (direct client call)')
      return result
    } catch (error) {
      if (error.name === 'AbortError') {
        // Preserve AbortError semantics so the stop button works
        throw error
      }
      throw error
    }
  }

  // Fallback: use Netlify server keys via the /api/ai-chat endpoint
  const response = await fetch('/api/ai-chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      messages,
      provider: 'kimi',
      model
      // Do NOT send user API keys to the server - server will use its own key
    }),
    signal: currentAbortController.signal
  })

  if (!response.ok) {
    // Handle 504 timeout specially
    if (response.status === 504) {
      throw new Error('⏱️ Response timed out (26s limit). Please add your own API key in Settings for unlimited response time!')
    }
    
    try {
      const error = await response.json()
      throw new Error(error.error || 'AI request failed. Try configuring your own API key in AI Settings.')
    } catch (jsonError) {
      // If JSON parsing fails, it's likely a timeout
      throw new Error('⏱️ Response timed out. Add your own API key in Settings for longer responses!')
    }
  }

  let data
  try {
    data = await response.json()
  } catch (jsonError) {
    // JSON parse error usually means timeout or incomplete response
    throw new Error('⏱️ Response incomplete (likely timeout). Please add your own API key in Settings to avoid timeouts!')
  }
  
  return data
}

// Direct Moonshot (Kimi) call from the browser when user API key is provided
async function callKimiDirect(messages, apiKey, model, signal) {
  // Separate system message
  const systemMessage = messages.find(m => m.role === 'system')
  const userMessages = messages.filter(m => m.role !== 'system')
  
  // Clean messages to only include role and content
  const cleanedMessages = userMessages.map(msg => ({
    role: msg.role,
    content: msg.content
  }))

  const fetchOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model || 'kimi-k2-turbo-preview',
      messages: [
        ...(systemMessage ? [{ role: 'system', content: systemMessage.content }] : []),
        ...cleanedMessages
      ],
      temperature: 0.6,
      max_tokens: 32768,
      stream: false
    })
  }

  if (signal) {
    fetchOptions.signal = signal
  }

  const response = await fetch('https://api.moonshot.ai/v1/chat/completions', fetchOptions)

  if (!response.ok) {
    let errorPayload
    try {
      errorPayload = await response.json()
    } catch {
      throw new Error('Kimi AI API error')
    }
    throw new Error(errorPayload.error?.message || 'Kimi AI API error')
  }

  const data = await response.json()

  return {
    content: data.choices?.[0]?.message?.content || '',
    usage: data.usage
  }
}
